import React, { useState, useEffect } from 'react';
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { productionItemsAPI } from '../services/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import './Production.css';

// Composant carrousel d'images
const ImageCarousel = ({ images, name, onImageClick }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  if (!images || images.length === 0) {
    return <div className="carousel-placeholder">Aucune image</div>;
  }

  const goToPrevious = (e) => {
    e.stopPropagation();
    setCurrentIndex(prev => prev === 0 ? images.length - 1 : prev - 1);
  };

  const goToNext = (e) => {
    e.stopPropagation();
    setCurrentIndex(prev => prev === images.length - 1 ? 0 : prev + 1);
  };

  return (
    <div className="image-carousel" onClick={onImageClick}>
      <img
        src={images[currentIndex]}
        alt={`${name} - ${currentIndex + 1}`}
        className="carousel-image"
      />
      {images.length > 1 && (
        <>
          <button className="carousel-btn carousel-btn-left" onClick={goToPrevious}>
            <ChevronLeft size={20} />
          </button>
          <button className="carousel-btn carousel-btn-right" onClick={goToNext}>
            <ChevronRight size={20} />
          </button>
          <div className="carousel-dots">
            {images.map((_, idx) => (
              <span
                key={idx}
                className={`carousel-dot ${idx === currentIndex ? 'active' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentIndex(idx);
                }}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const Production = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [dialogImageIndex, setDialogImageIndex] = useState(0);

  useEffect(() => {
    loadItems();
  }, []);

  const loadItems = async () => {
    try {
      setLoading(true);
      const data = await productionItemsAPI.getAll();
      setItems(data);
    } catch (error) {
      console.error('Error loading production items:', error);
    } finally {
      setLoading(false);
    }
  };

  // Sort items - featured first
  const sortedItems = [...items].sort((a, b) => {
    if (a.featured && !b.featured) return -1;
    if (!a.featured && b.featured) return 1;
    return 0;
  });

  return (
    <div className="production">
      {/* Partners Section */}
      <div className="partners-section">
        <div className="container">
          <img 
            src="https://customer-assets.emergentagent.com/job_phoenix-store-1/artifacts/k1zjg6u6_Ils%20nous%20font%20confiance.png" 
            alt="Ils nous font confiance - Nos partenaires"
            className="partners-image"
          />
        </div>
      </div>

      <div className="production-content section-padding">
        <div className="container">
          {loading ? (
            <div className="loading-text">Chargement des produits...</div>
          ) : sortedItems.length === 0 ? (
            <div className="no-items">
              <p>Aucun produit de production disponible pour le moment</p>
            </div>
          ) : (
            <div className="items-grid">
              {sortedItems.map((item) => (
                <div key={item.id} className="item-card premium-card fade-in-up">
                  <div className="item-image-wrapper">
                    <ImageCarousel 
                      images={item.images} 
                      name={item.name}
                      onImageClick={() => {
                        setSelectedItem(item);
                        setDialogImageIndex(0);
                      }}
                    />
                    {item.featured && (
                      <div className="item-badge">Recommandé</div>
                    )}
                  </div>
                  <div className="item-info">
                    <h3 className="item-name">{item.name}</h3>
                    {item.dimensions && (
                      <p className="item-dimensions">📐 {item.dimensions}</p>
                    )}
                    {item.min_quantity && item.min_quantity > 1 && (
                      <p className="item-min-qty">📦 Minimum: {item.min_quantity} unités</p>
                    )}
                    <div className="item-footer">
                      <span className="item-price">{item.price.toFixed(2)} € HT</span>
                      <button 
                        onClick={() => {
                          setSelectedItem(item);
                          setDialogImageIndex(0);
                        }}
                        className="item-link-button"
                      >
                        Voir détails
                        <ArrowRight size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Item Details Dialog */}
      <Dialog open={selectedItem !== null} onOpenChange={() => setSelectedItem(null)}>
        <DialogContent className="item-dialog">
          {selectedItem && (
            <div className="dialog-scroll-container">
              <DialogHeader>
                <DialogTitle>{selectedItem.name}</DialogTitle>
              </DialogHeader>
              <div className="dialog-item-content">
                {/* Image gallery with navigation */}
                <div className="dialog-images">
                  {selectedItem.images && selectedItem.images.length > 0 ? (
                    <>
                      <div className="dialog-main-image-container">
                        <img
                          src={selectedItem.images[dialogImageIndex]}
                          alt={selectedItem.name}
                          className="dialog-item-image"
                        />
                        {selectedItem.images.length > 1 && (
                          <>
                            <button 
                              className="dialog-nav-btn dialog-nav-left"
                              onClick={() => setDialogImageIndex(prev => prev === 0 ? selectedItem.images.length - 1 : prev - 1)}
                            >
                              <ChevronLeft size={24} />
                            </button>
                            <button 
                              className="dialog-nav-btn dialog-nav-right"
                              onClick={() => setDialogImageIndex(prev => prev === selectedItem.images.length - 1 ? 0 : prev + 1)}
                            >
                              <ChevronRight size={24} />
                            </button>
                          </>
                        )}
                      </div>
                      {selectedItem.images.length > 1 && (
                        <div className="dialog-image-thumbnails">
                          {selectedItem.images.map((img, idx) => (
                            <img
                              key={idx}
                              src={img}
                              alt={`${selectedItem.name} ${idx + 1}`}
                              className={`dialog-thumbnail ${idx === dialogImageIndex ? 'active' : ''}`}
                              onClick={() => setDialogImageIndex(idx)}
                            />
                          ))}
                        </div>
                      )}
                      <div className="dialog-image-counter">
                        {dialogImageIndex + 1} / {selectedItem.images.length}
                      </div>
                    </>
                  ) : (
                    <div className="dialog-no-image">Aucune image disponible</div>
                  )}
                </div>
                <div className="dialog-item-info">
                  <p className="dialog-description">{selectedItem.description}</p>
                  {selectedItem.dimensions && (
                    <p className="dialog-details"><strong>📐 Dimensions:</strong> {selectedItem.dimensions}</p>
                  )}
                  {selectedItem.min_quantity && selectedItem.min_quantity > 1 && (
                    <p className="dialog-details"><strong>📦 Quantité minimum:</strong> {selectedItem.min_quantity} unités</p>
                  )}
                  <div className="dialog-price">{selectedItem.price.toFixed(2)} € HT</div>
                  <Link to="/contact" className="btn-primary" onClick={() => setSelectedItem(null)}>
                    Demander un devis
                    <ArrowRight size={20} />
                  </Link>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Production;
